'use client';

import { sendErrorToBackend } from '@/core/lib/api/main/errorReporter';
import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Heading } from '@/components/ui/Heading';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const DefaultErrorFallback = ({
  onReset,
  error,
}: {
  onReset: () => void;
  error?: Error;
}) => {
  const { dictionary } = useDictionary();

  return (
    <div className="flex min-h-[300px] w-full items-center justify-center p-8">
      <div className="flex flex-col items-center rounded-lg bg-white p-8 shadow-lg">
        <AlertTriangle className="text-red-600" size={48} />
        <Heading level={2} className="mt-4 text-center">
          {dictionary.errors?.componentError}
        </Heading>

        <Button onClick={onReset} className="mt-8">
          {dictionary.errors?.retry}
        </Button>
        {process.env.NEXT_PUBLIC_APP_ENV === 'development' && error && (
          <Collapsible className="mt-8">
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                className='text-primary-600'
              >
                {dictionary.errors.moreDetails}
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-6">
              <p className='justify-end flex flex-wrap text-sm text-red-600'>
                {error.message}
              </p>

            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: undefined,
    errorInfo: undefined,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    sendErrorToBackend({
      message: error.message,
      file: 'Client ErrorBoundary',
      line: 1,
      stack_trace: `${error.stack || 'No stack'}\nComponent stack: ${errorInfo.componentStack}`,
      url: window.location.href,
    });

    this.setState({ errorInfo });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <DefaultErrorFallback
          onReset={this.resetError}
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}